import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { HiPlus, HiSearch, HiPencil, HiTrash } from 'react-icons/hi';
import { Button, Input, Card, CardHeader, Table, Badge, Pagination, Modal } from '../components/ui';
import { listProducts, createProduct, updateProduct, deleteProduct } from '../services/products.api';
import { useAuthStore } from '../store/authStore';
import { formatDate } from '../utils/helpers';
import { getErrorMessage } from '../services/api';
import type { Product } from '../types';

export function Products() {
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'OWNER';

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['products', { page, search }],
    queryFn: () => listProducts({ page, search, includeInactive: true }),
  });

  const createMutation = useMutation({
    mutationFn: () => createProduct({ name, description }),
    onSuccess: () => {
      toast.success('Product created successfully');
      queryClient.invalidateQueries({ queryKey: ['products'] });
      closeModal();
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const updateMutation = useMutation({
    mutationFn: () => updateProduct(editingProduct!.id, { name, description }),
    onSuccess: () => {
      toast.success('Product updated successfully');
      queryClient.invalidateQueries({ queryKey: ['products'] });
      closeModal();
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteProduct(id),
    onSuccess: () => {
      toast.success('Product deactivated');
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const openCreateModal = () => {
    setEditingProduct(null);
    setName('');
    setDescription('');
    setIsModalOpen(true);
  };

  const openEditModal = (product: Product) => {
    setEditingProduct(product);
    setName(product.name);
    setDescription(product.description || '');
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingProduct(null);
    setName('');
    setDescription('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingProduct) {
      updateMutation.mutate();
    } else {
      createMutation.mutate();
    }
  };

  const columns = [
    { key: 'name', header: 'Name' },
    {
      key: 'description',
      header: 'Description',
      render: (p: Product) => p.description || '-',
    },
    {
      key: 'isActive',
      header: 'Status',
      render: (p: Product) => (
        <Badge status={p.isActive ? 'ACTIVE' : 'EXPIRED'}>
          {p.isActive ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      key: 'licenses',
      header: 'Licenses',
      render: (p: Product) => p._count?.licenses ?? 0,
    },
    {
      key: 'createdAt',
      header: 'Created',
      render: (p: Product) => formatDate(p.createdAt),
    },
    {
      key: 'actions',
      header: '',
      render: (p: Product) =>
        isAdmin && (
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => openEditModal(p)}>
              <HiPencil className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (confirm('Deactivate this product?')) {
                  deleteMutation.mutate(p.id);
                }
              }}
            >
              <HiTrash className="w-4 h-4 text-red-500" />
            </Button>
          </div>
        ),
    },
  ];

  return (
    <div>
      <CardHeader
        title="Products"
        description="Manage product catalog"
        action={
          isAdmin && (
            <Button onClick={openCreateModal}>
              <HiPlus className="w-4 h-4 mr-2" />
              New Product
            </Button>
          )
        }
      />

      <Card className="mb-6" padding={false}>
        <div className="p-4 border-b border-gray-200">
          <div className="relative max-w-md">
            <HiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              placeholder="Search products..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        <Table
          columns={columns}
          data={data?.data || []}
          keyExtractor={(product) => product.id}
          isLoading={isLoading}
          emptyMessage="No products found"
        />

        {data && data.totalPages > 1 && (
          <Pagination
            page={page}
            totalPages={data.totalPages}
            total={data.total}
            limit={data.limit}
            onPageChange={setPage}
          />
        )}
      </Card>

      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={editingProduct ? 'Edit Product' : 'Create New Product'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="secondary" onClick={closeModal}>
              Cancel
            </Button>
            <Button
              type="submit"
              isLoading={createMutation.isPending || updateMutation.isPending}
            >
              {editingProduct ? 'Update Product' : 'Create Product'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
